<template>
  <div class="report-access-selection-controls">
    <div class="report-access-selection-controls__header">
      <div
        class="flex pb-16 items-center text-neutral-text-strong font-bold text-lg"
      >
        <BittsButton
          v-if="usersSelected"
          data-testid="user-perm-back-button"
          :center-icon="['fak', 'arrow-left']"
          aria-label="Back"
          variant="ghost"
          class="mr-8"
          size="small"
          @click="onClearSelected"
        />
        Share "{{ report.name }}"
      </div>

      <template v-if="!usersSelected">
        <p class="text-neutral-text-strong pb-8 font-bold"
          >Share with a partner</p
        >
        <BittsSelect
          v-model="selectedOrganization"
          data-testid="organization-select"
          option-type="company"
          placeholder="Select organization"
          :options="organizationOptions"
          :use-disabled-options="!allowExternalSharing"
          :disabled-option-content="{
            text: externalSharingDisabledReason,
          }"
          :tooltip-custom-props="{ mouseEnterDelay: 0 }"
          :prefix-icon="['fak', 'partner']"
          dropdown-class-name="report-access-selection-controls__dropdown"
          class="mb-8"
        >
          <template #option="{ option }">
            <div
              class="flex items-center w-full"
              :class="{
                'cursor-not-allowed opacity-50':
                  !allowExternalSharing && option.value !== 'own',
              }"
            >
              <BittsAvatar
                size="x-small"
                :show-initials="true"
                :org="option"
                class="mr-8"
              />
              <span class="flex-1">{{ option.label }}</span>
              <BittsTag
                v-if="option.value === 'own'"
                color="neutral"
                size="x-small"
                class="ml-8"
              >
                Your organization
              </BittsTag>
            </div>
          </template>
        </BittsSelect>

        <BittsSelect
          v-model="selectedUser"
          data-testid="user-select"
          option-type="entity"
          placeholder="Select user"
          :options="availableUserOptions"
          :disabled="!selectedOrganization || isLoadingUsers"
          :prefix-icon="['far', 'user']"
          :none-remaining-message="userPickerEmptyMessage"
          dropdown-class-name="report-access-selection-controls__dropdown"
          class="mb-0"
        >
          <template #option="{ option }">
            <div class="flex items-center w-full report-access-user-option">
              <BittsAvatar
                size="small"
                :show-initials="true"
                :force-circle="!option.picture_url"
                :user="option"
                :org="
                  option.organization_id != null &&
                  option.organization_id !== currentOrg?.id
                    ? option.org
                    : undefined
                "
                class="mr-10"
              />
              <div class="flex-1 min-w-0">
                <div class="report-access-user-option__name">
                  {{
                    getUserLabel(
                      option.first_name,
                      option.last_name,
                      option.email,
                    )
                  }}
                </div>
                <div
                  class="text-sm text-neutral-text-weak truncate report-access-user-option__email"
                >
                  {{ option.email }}
                </div>
              </div>
              <BittsTag
                v-if="option.role"
                color="info"
                size="x-small"
                class="ml-8 shrink-0 report-access-user-option__role"
              >
                {{ option.role }}
              </BittsTag>
            </div>
          </template>
        </BittsSelect>
      </template>

      <template v-else>
        <div
          v-for="(pending, index) in pendingShareUsers"
          :key="`row-${index}-${pending.userOption.user_id}`"
          class="report-access-selection-controls__pending-row"
        >
          <BittsSelect
            :model-value="pending.userOption.user_id"
            :options="getAvailableUserOptionsForRow(index)"
            option-type="entity"
            :disabled="!selectedOrganization || isLoadingUsers || isLoading"
            :prefix-icon="['far', 'user']"
            :none-remaining-message="userPickerEmptyMessage"
            dropdown-class-name="report-access-selection-controls__dropdown"
            class="report-access-selection-controls__pending-user-picker"
            data-testid="pending-row-user-picker"
            @update:model-value="
              (userId: number) => onPendingRowUserChange(index, userId)
            "
          >
            <template #option="{ option }">
              <div class="flex items-center w-full report-access-user-option">
                <BittsAvatar
                  size="small"
                  :show-initials="true"
                  :force-circle="!option.picture_url"
                  :user="option"
                  :org="
                    option.organization_id != null &&
                    option.organization_id !== currentOrg?.id
                      ? option.org
                      : undefined
                  "
                  class="mr-10"
                />
                <div class="flex-1 min-w-0">
                  <div class="report-access-user-option__name">
                    {{
                      getUserLabel(
                        option.first_name,
                        option.last_name,
                        option.email,
                      )
                    }}
                  </div>
                  <div
                    class="text-sm text-neutral-text-weak truncate report-access-user-option__email"
                  >
                    {{ option.email }}
                  </div>
                </div>
                <BittsTag
                  v-if="option.role"
                  color="info"
                  size="x-small"
                  class="ml-8 shrink-0 report-access-user-option__role"
                >
                  {{ option.role }}
                </BittsTag>
              </div>
            </template>
          </BittsSelect>
          <ReportRolePicker
            :model-value="pending.accessLevel"
            :eligible-permissions="
              getEligiblePermissionsForPendingUser(pending)
            "
            :disabled="isLoading"
            :show-remove="false"
            class="report-access-selection-controls__role-picker"
            data-testid="pending-user-role-picker"
            :dropdown-match-select-width="false"
            @update:model-value="
              (val: ReportAccessLevel | 'remove_user') => {
                if (val === 'remove_user') return;
                onPendingUserRoleChange(index, val);
              }
            "
          />
        </div>
        <div class="report-access-selection-controls__add-more-wrap">
          <component :is="addMoreWrapperComponent" v-bind="addMoreWrapperProps">
            <BittsButton
              type="neutral"
              variant="ghost"
              size="x-small"
              text="Add more people"
              :left-icon="['far', 'plus']"
              data-testid="add-more-people-button"
              class="report-access-selection-controls__add-more-button"
              :disabled="
                !canAddMorePeople || isLoading || showShareErrorMessage
              "
              @click="onAddMorePeopleClick"
            />
            <template v-if="!canAddMorePeople" #title>
              No one left to add
            </template>
          </component>
        </div>
      </template>
    </div>

    <template v-if="!usersSelected">
      <BittsDivider class="my-8" />
      <div class="report-access-selection-controls__scrollable">
        <p class="text-neutral-text-strong pb-8 font-bold">List Access</p>

        <div
          v-if="isLoadingAccess"
          class="flex items-center justify-center py-24"
        >
          <BittsLoading :is-loading="true" :icon-width="40" />
        </div>
        <template v-else>
          <ReportUserAccessCard
            v-for="user in sortedAllUsers"
            :key="getUserAccessKey(user)"
            :user="user"
            :is-me="user.user_id === currentUser?.id"
            :is-creator="isCreator(user)"
            :loading="loadingState[getUserAccessKey(user)]"
            :editable="canAccessBeEdited(user)"
            :org="getOrgForUser(user)"
            :report-organization-id="report.organization_id"
            :eligible-permissions="user.eligible_report_permissions"
            @option-selected="onClickedRemoveUser"
          />
        </template>
      </div>
      <div>
        <BittsDivider class="mb-8 mt-0" />
        <p class="text-neutral-text-strong pb-8 font-bold">General Access</p>
        <GeneralAccessSelector v-model="generalAccessLevel" />
        <BittsDivider class="my-8" />
      </div>
    </template>
    <div class="report-access-selection-controls__footer">
      <BittsCallout
        v-if="!showShareErrorMessage"
        title="List sharing works independently from your default sharing settings"
        subtitle="All fields included in this list will be visible to your partner unless they’re marked as private"
        size="x-small"
        :icon="['fak', 'share']"
      />
      <BittsCallout
        v-if="
          hasMixedCountsOnlySharing && selectedOrganization && !isOwnOrgSelected
        "
        title="Some accounts won’t be visible to your partner"
        subtitle="This List includes a Population that is set to Counts Only sharing. Partners can’t see the accounts in that Population, so they will only see accounts from Populations that are currently shared. To make these accounts visible to your partner, update your Population sharing settings."
        type="warning"
        size="x-small"
        :class="!showShareErrorMessage ? 'mt-8' : ''"
      />
      <template v-if="usersSelected">
        <BittsCallout
          v-if="showShareErrorMessage"
          title="This list contains fields not shared with this partner"
          subtitle="Remove those fields or request Manage Data Sharing permission to share this list"
          type="warning"
          size="x-small"
          class="my-16" />
        <BittsDivider class="my-16" />
        <div class="flex justify-between mt-16">
          <BittsButton
            text="Cancel"
            class="mr-16"
            type="neutral"
            :loading="isLoading"
            @click="onClearSelected"
          />
          <BittsButton
            text="Share"
            :loading="isLoading"
            :disabled="showShareErrorMessage"
            @click="onClickedSendInvite"
          /> </div
      ></template>
      <template v-else>
        <footer class="flex justify-between items-center pt-16">
          <BittsCallout
            :title="reportAccessInfo.text"
            size="x-small"
            class="select-none cursor-pointer"
            @click="onLearnMore"
          />
          <BittsTooltip
            trigger="hover"
            placement="bottomRight"
            overlay-class="c-report-access-selection-controls__tooltip"
            :mount-to-body="true"
          >
            <BittsButton
              :left-icon="['fak', 'link']"
              text="Copy link"
              variant="outline"
              type="neutral"
              size="small"
              @click="onCopyReportLink"
            />
            <template #title>
              Users with a Crossbeam seat in your organization or your partner
              organization can access a view-only version of this List
            </template>
          </BittsTooltip>
        </footer>
        <BittsDivider class="my-8" />
        <div class="flex justify-between mt-16">
          <BittsButton
            text="Cancel"
            class="mr-16"
            type="neutral"
            :disabled="isSavingGeneralAccess"
            @click="onCancelGeneralAccess"
          />
          <BittsButton
            text="Save"
            :loading="isSavingGeneralAccess"
            :disabled="!report.id"
            @click="onSaveGeneralAccess"
          />
        </div>
      </template>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  BittsAvatar,
  BittsButton,
  BittsCallout,
  BittsDivider,
  BittsLoading,
  BittsSelect,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { capitalize, orderBy, sortBy } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';

import GeneralAccessSelector from '@/components/reports/GeneralAccessSelector.vue';
import ReportRolePicker from '@/components/reports/ReportRolePicker.vue';
import ReportUserAccessCard, {
  Option,
} from '@/components/reports/ReportUserAccessCard.vue';

import { crossbeamApi } from '@/api';
import useReportTeamMembers from '@/composables/reports/useReportTeamMembers';
import useAuth from '@/composables/useAuth';
import useIteratively from '@/composables/useIteratively';
import { GENERAL_ACCESS_ONLY_INVITED } from '@/constants/reports';
import {
  useDataSharesStore,
  useFlashesStore,
  usePartnersStore,
  usePopulationsStore,
  useTeamStore,
} from '@/stores';
import {
  BulkTeamAccessAuthorization,
  GeneralAccessLevel,
  PartnerOrganizationWithAccess,
  PartnerTeamAccessAuthorization,
  ReportAccessLevel,
  ReportUserAccessDeletePayload,
  ReportUserAccessWithPartner,
  UserWithReportAccess,
} from '@/types/report_team_access';
import {
  Report,
  ReportOurSegmentFiltersItem,
  ReportPartnerSegmentFiltersItem,
  TrimmedReport,
} from '@/types/reports';
import { copyToClipBoard } from '@/utils';
import { getHighestEligiblePermission } from '@/utils/reportPermissions';

const props = defineProps<{
  report: Report | TrimmedReport;
}>();

const emit = defineEmits<{ close: [] }>();

const { report } = toRefs(props);

const partnersStore = usePartnersStore();
const dataSharesStore = useDataSharesStore();
const flashesStore = useFlashesStore();
const populationsStore = usePopulationsStore();
const { revokedUsers } = storeToRefs(useTeamStore());
const { currentUser, currentOrg, hasScope } = useAuth();
const route = useRoute();
const { iteratively } = useIteratively();

const selectedOrganization = ref<string | null>(null);
const selectedUser = ref<number | null>(null);
const isLoading = ref(false);
const isSavingGeneralAccess = ref(false);
const savedGeneralAccessLevel = ref<GeneralAccessLevel>(
  report.value.general_access ?? GENERAL_ACCESS_ONLY_INVITED,
);
const generalAccessLevel = ref<GeneralAccessLevel | null>(
  savedGeneralAccessLevel.value,
);

type PendingShareUser = {
  userOption: UserOption;
  accessLevel: ReportAccessLevel;
};
const pendingShareUsers = ref<PendingShareUser[]>([]);
const UNSHARED_COLUMNS_ERROR_HEADER =
  'This list contains fields not shared with this partner';
const UNSHARED_COLUMNS_ERROR_DESCRIPTION =
  'Remove those fields or request Manage Data Sharing permission to share this list';
const showShareErrorMessage = ref(false);
const isLoadingUsers = ref(false);
const isLoadingAccess = ref(true);
type LoadingState = Record<string, boolean>;
const loadingState = ref<LoadingState>({});

function getUserAccessKey(
  userIdOrUser: number | { user_id: number; organization_id?: number | null },
  organizationId?: number | null,
): string {
  if (typeof userIdOrUser === 'number') {
    return `${userIdOrUser}-${organizationId ?? currentOrg.value.id}`;
  }
  return `${userIdOrUser.user_id}-${userIdOrUser.organization_id ?? currentOrg.value.id}`;
}

const { teamMembers, getTeamMembers } = useReportTeamMembers(report);
const partnerTeamMembers = ref<PartnerTeamAccessAuthorization[]>([]);
const partnerOrgsWithAccess = ref<PartnerOrganizationWithAccess[]>([]);

const reportAccessInfo = {
  text: 'Learn more about List access',
  url: 'https://help.crossbeam.com/en/articles/11801904-list-beta',
};

function onLearnMore() {
  window.open(reportAccessInfo.url, '_blank', 'noopener,noreferrer');
}

type OrgInfo = {
  name: string;
  logo_url?: string | null;
  clearbit_domain?: string | null;
  domain?: string | null;
};

type OrgOption = {
  value: string;
  label: string;
  name: string;
  uuid?: string;
  logo_url?: string | null;
  clearbit_domain?: string | null;
  domain?: string | null;
  disabled?: boolean;
};

function createOrgInfoFromOrg(org: {
  name: string;
  logo_url?: string | null;
  clearbit_domain?: string | null;
  domain?: string | null;
}): OrgInfo {
  return {
    name: org.name,
    logo_url: org.logo_url,
    clearbit_domain: org.clearbit_domain,
    domain: org.domain,
  };
}

const canShareExternally = computed(() => hasScope('write:lists'));

/** True when this report shape allows sharing with an external (partner) org: single partner, overlaps list. */
const isReportEligibleForExternalSharing = computed(() => {
  const isNotGreenfield =
    !report.value?.consolidated_report_type.includes('greenfield');
  const partnerItems = report.value?.partner_segment_filters?.items;

  if (partnerItems?.length) {
    if (
      partnerItems.some(
        (filter) => !('organization_id' in filter) || !filter.organization_id,
      )
    )
      return false;
    const orgIds = partnerItems.reduce((acc, item) => {
      if ('organization_id' in item) acc.add(item.organization_id);
      return acc;
    }, new Set<number>());
    return isNotGreenfield && orgIds.size === 1;
  }

  return (
    isNotGreenfield && report.value?.partner_organization_ids?.length === 1
  );
});

function extractPopulationIdsFromSegmentFilters(
  items:
    | ReportOurSegmentFiltersItem[]
    | ReportPartnerSegmentFiltersItem[]
    | undefined,
  isPartner: boolean,
): number[] {
  if (!items?.length) return [];

  if (isPartner) {
    const cache = new Map<string, number[]>();

    return items.flatMap((item) => {
      if ('population_ids' in item && item.population_ids?.length) {
        return item.population_ids;
      }
      if (item.segment) {
        const partnerItem = item as ReportPartnerSegmentFiltersItem;
        const orgId =
          'organization_id' in partnerItem ? partnerItem.organization_id : 0;
        const key = `${orgId || 'wildcard'}__${item.segment}`;

        if (!cache.has(key)) {
          const popIds = populationsStore
            .getPartnerPopulationsBySegmentAndPartner(item.segment, orgId)
            .map((pop) => pop.id);
          cache.set(key, popIds);
        }

        return cache.get(key) || [];
      }
      return [];
    });
  }

  const cache = new Map<string, number[]>();

  return items.flatMap((item) => {
    if ('population_ids' in item && item.population_ids?.length) {
      return item.population_ids;
    }
    if (item.segment) {
      if (!cache.has(item.segment)) {
        const popIds = populationsStore
          .getPopulationsBySegment(item.segment)
          .map((pop) => pop.id);
        cache.set(item.segment, popIds);
      }

      return cache.get(item.segment) || [];
    }
    return [];
  });
}

type ReportSharingContextType = {
  ourPopulationIds: number[];
  partnerOrgId: number;
  partnerPopulationIds: number[] | undefined;
};

const reportSharingContext = computed<ReportSharingContextType | null>(() => {
  if (!isReportEligibleForExternalSharing.value) return null;
  const r = report.value;
  const item = r.partner_segment_filters?.items?.[0];
  const partnerOrgId = Number(
    item && 'organization_id' in item ? item.organization_id : 0,
  );
  const ourPopulationIds = extractPopulationIdsFromSegmentFilters(
    r.our_segment_filters?.items,
    false,
  );
  const partnerPopulationIds = extractPopulationIdsFromSegmentFilters(
    r.partner_segment_filters?.items,
    true,
  );
  return {
    ourPopulationIds,
    partnerOrgId,
    partnerPopulationIds:
      partnerPopulationIds.length > 0 ? partnerPopulationIds : undefined,
  };
});

const isOnlyOverlapCountsSharedWithPartnerOrg = computed(() => {
  const ctx = reportSharingContext.value;
  if (!ctx) return false;
  return dataSharesStore.isOnlyOverlapCountsSharedWithPartner(
    ctx.ourPopulationIds,
    ctx.partnerOrgId,
    ctx.partnerPopulationIds,
  );
});

const hasMixedCountsOnlySharing = computed(() => {
  const ctx = reportSharingContext.value;
  if (!ctx) return false;
  return dataSharesStore.hasMixedSharingWithPartner(
    ctx.ourPopulationIds,
    ctx.partnerOrgId,
    ctx.partnerPopulationIds,
  );
});

const externalSharingDisabledReason = computed(() => {
  if (!canShareExternally.value) {
    return 'Only users with manage access for Partner Shared Lists can share Lists with partners';
  }
  if (
    isReportEligibleForExternalSharing.value &&
    isOnlyOverlapCountsSharedWithPartnerOrg.value
  ) {
    return 'You can only share Lists externally when you share record-level data with your partner, not just overlap counts';
  }
  return 'Only single partner Lists can be shared. Create a new List for one partner to share';
});

const allowExternalSharing = computed(
  () =>
    canShareExternally.value &&
    isReportEligibleForExternalSharing.value &&
    !isOnlyOverlapCountsSharedWithPartnerOrg.value,
);

const organizationOptions = computed<OrgOption[]>(() => {
  const ownOrgInfo = createOrgInfoFromOrg(currentOrg.value);
  const ownOrgOption: OrgOption = {
    value: 'own',
    label: currentOrg.value.name,
    ...ownOrgInfo,
  };

  if (isReportFromExternalOrg.value) {
    return [ownOrgOption];
  }

  const orgIdsWithAccess = new Set(
    existingUsers.value
      .map((u) => u.organization_id)
      .filter((id): id is number => id != null && id !== currentOrg.value.id),
  );

  const availablePartnerOrgs = (
    orgIdsWithAccess.size > 0
      ? partnerOrgsWithAccess.value.filter((org) =>
          orgIdsWithAccess.has(org.id),
        )
      : partnerOrgsWithAccess.value
  ).filter((org) => {
    const partner = partnersStore.getPartnerOrgByUuid(org.uuid);
    if (!partner) return false;
    return !partner.offline_partner && !partner.virtual_partner;
  });

  const partnerOptions: OrgOption[] = availablePartnerOrgs.map(
    (partnerAccess) => {
      const partnerFromStore = partnersStore.getPartnerOrgByUuid(
        partnerAccess.uuid,
      );
      if (!partnerFromStore) {
        return {
          value: partnerAccess.uuid,
          label: partnerAccess.name,
          name: partnerAccess.name,
          uuid: partnerAccess.uuid,
          logo_url: null,
          clearbit_domain: null,
          domain: null,
          disabled: !allowExternalSharing.value,
        };
      }
      const partnerOrgInfo = createOrgInfoFromOrg(partnerFromStore);
      return {
        value: partnerAccess.uuid,
        label: partnerAccess.name,
        uuid: partnerAccess.uuid,
        disabled: !allowExternalSharing.value,
        ...partnerOrgInfo,
      };
    },
  );

  return [ownOrgOption, ...partnerOptions];
});

const isOwnOrgSelected = computed(() => selectedOrganization.value === 'own');

const existingUsers = computed<UserWithReportAccess[]>(() => {
  if (!report.value?.user_access) return [];
  const userAccessList = report.value
    .user_access as ReportUserAccessWithPartner[];
  const revokedUsersById = new Map(revokedUsers.value.map((u) => [u.id, u]));
  return userAccessList.map((userAccess) => {
    const revokedUser = revokedUsersById.get(userAccess.user_id);
    const user =
      userAccess.user ||
      (userAccess.user_id === currentUser.value?.id
        ? currentUser.value
        : null) ||
      (revokedUser
        ? {
            ...revokedUser,
            last_name: `${revokedUser.last_name} (Deactivated)`,
          }
        : { first_name: 'Admin', last_name: '' });

    return {
      ...userAccess,
      ...user,
      organization_id: userAccess.organization_id,
    } as UserWithReportAccess;
  });
});

const sortedAllUsers = computed<UserWithReportAccess[]>(() => {
  const isOwnOrgUser = (u: UserWithReportAccess) =>
    !u.organization_id || u.organization_id === currentOrg.value.id;
  const ownOrgUsers = existingUsers.value.filter(isOwnOrgUser);
  const partnerUsers = existingUsers.value.filter((u) => !isOwnOrgUser(u));

  const sortedOwnOrg = orderBy(
    ownOrgUsers,
    [(u) => Number(isCreator(u)), 'first_name', 'last_name'],
    ['desc', 'asc', 'asc'],
  );

  const sortedPartners = orderBy(
    partnerUsers,
    ['first_name', 'last_name'],
    ['asc', 'asc'],
  );

  return [...sortedOwnOrg, ...sortedPartners];
});

function getOrgForUser(user: UserWithReportAccess): OrgInfo | null {
  if (!user.organization_id || user.organization_id === currentOrg.value.id) {
    return null;
  }
  const partnerOrg = partnersStore.getPartnerOrgById(user.organization_id);
  if (partnerOrg) {
    return createOrgInfoFromOrg(partnerOrg);
  }
  const accessOrg = partnerOrgsWithAccess.value.find(
    (o) => o.id === user.organization_id,
  );
  if (accessOrg) {
    return { name: accessOrg.name };
  }
  return null;
}

type UserOption = {
  value: number;
  label: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  picture_url?: string | null;
  user_id: number;
  organization_id: number;
  org?: OrgInfo | null;
  role?: string | null;
};

function getUserLabel(
  firstName: string | null,
  lastName: string | null,
  email: string,
): string {
  const nameParts = [firstName, lastName].filter(Boolean);
  return nameParts.length > 0 ? nameParts.join(' ') : email;
}

const UNSHARED_COLUMNS_ERROR_PHRASE =
  'Data Sharing:Manage permissions can share reports containing unshared data';

function isReportUserAccessUnsharedColumnsError(error: unknown): boolean {
  const message = Array.isArray(error)
    ? error[0]
    : (error as { errors?: string[] })?.errors?.[0];
  return (
    typeof message === 'string' &&
    message.includes(UNSHARED_COLUMNS_ERROR_PHRASE)
  );
}

function getUsersFromAuthorizations(
  authorizations: (
    | BulkTeamAccessAuthorization
    | PartnerTeamAccessAuthorization
  )[],
  excludeUserIds: Set<number>,
  organizationId: number,
  org?: OrgInfo | null,
): UserOption[] {
  const users: UserOption[] = [];
  authorizations.forEach((auth) => {
    const user = auth.user;
    if (!user) return;
    if (excludeUserIds.has(user.id)) return;

    const rawRole =
      user.department || user.job_title || user.company_position || null;
    const role = rawRole ? capitalize(rawRole) : null;

    users.push({
      value: user.id,
      label: getUserLabel(user.first_name, user.last_name, user.email),
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      picture_url: user.picture_url || null,
      user_id: user.id,
      organization_id: organizationId,
      org,
      role,
    });
  });
  return users;
}

function getAvailableUserOptions(excludeRowIndex?: number): UserOption[] {
  if (!selectedOrganization.value) return [];

  const selectedOrgUuid = selectedOrganization.value;
  const existingIds = new Set(
    existingUsers.value
      .filter((u) => {
        if (isOwnOrgSelected.value) {
          return (
            !u.organization_id || u.organization_id === currentOrg.value.id
          );
        }
        const partnerOrg = partnersStore.getPartnerOrgByUuid(selectedOrgUuid);
        return u.organization_id === partnerOrg?.id;
      })
      .map((u) => u.user_id),
  );
  const pendingForOrg = pendingShareUsers.value.filter((p, j) => {
    if (excludeRowIndex !== undefined && j === excludeRowIndex) return false;
    if (isOwnOrgSelected.value) {
      return p.userOption.organization_id === currentOrg.value.id;
    }
    const partnerOrg = partnersStore.getPartnerOrgByUuid(selectedOrgUuid);
    return partnerOrg != null && p.userOption.organization_id === partnerOrg.id;
  });
  const pendingIds = new Set(pendingForOrg.map((p) => p.userOption.user_id));
  const existingUserIds = new Set([...existingIds, ...pendingIds]);

  let organizationId: number;
  let org: OrgInfo | undefined;

  if (isOwnOrgSelected.value) {
    organizationId = currentOrg.value.id;
    org = createOrgInfoFromOrg(currentOrg.value);
    const users = getUsersFromAuthorizations(
      teamMembers.value,
      existingUserIds,
      organizationId,
      org,
    );
    return sortBy(users, ['label']);
  }

  const partnerOrg = partnersStore.getPartnerOrgByUuid(selectedOrgUuid);
  if (!partnerOrg) return [];

  organizationId = partnerOrg.id;
  org = createOrgInfoFromOrg(partnerOrg);

  const users = getUsersFromAuthorizations(
    partnerTeamMembers.value,
    existingUserIds,
    organizationId,
    org,
  );
  return sortBy(users, ['label']);
}

const availableUserOptions = computed<UserOption[]>(() =>
  getAvailableUserOptions(),
);

function getAvailableUserOptionsForRow(rowIndex: number): UserOption[] {
  return getAvailableUserOptions(rowIndex);
}

const userPickerEmptyMessage = computed(() => {
  if (
    selectedOrganization.value &&
    availableUserOptions.value.length === 0 &&
    !isLoadingUsers.value
  ) {
    return 'No one left to add';
  }
  return 'No results found';
});

const usersSelected = computed(() => pendingShareUsers.value.length > 0);

async function getPartnerOrgsWithAccess() {
  const { data, error } = await crossbeamApi.GET(
    '/v0.1/reports/{uuid}/partner-access',
    { params: { path: { uuid: report.value.id } } },
  );
  if (error) throw error;
  partnerOrgsWithAccess.value = data?.partner_organizations || [];
}

async function getPartnerTeamMembers(partnerOrgUuid: string) {
  isLoadingUsers.value = true;
  try {
    const { data, error } = await crossbeamApi.GET(
      '/v0.2/reports/{uuid}/partner-team-access',
      {
        params: {
          path: { uuid: report.value.id },
          query: { partner_organization_uuid: partnerOrgUuid },
        },
      },
    );
    if (error) throw error;
    partnerTeamMembers.value = data?.authorizations || [];
  } finally {
    isLoadingUsers.value = false;
  }
}

watch(selectedUser, (userId) => {
  if (userId == null) return;
  const userOption = availableUserOptions.value.find((u) => u.value === userId);
  if (!userOption) return;
  const isAlreadyPending = pendingShareUsers.value.some(
    (p) =>
      p.userOption.user_id === userOption.user_id &&
      p.userOption.organization_id === userOption.organization_id,
  );
  if (isAlreadyPending) {
    selectedUser.value = null;
    return;
  }
  const authorizations = isOwnOrgSelected.value
    ? teamMembers.value
    : partnerTeamMembers.value;
  const userAuth = authorizations.find(
    (auth) => auth.user?.id === userOption.user_id,
  );
  const accessLevel = getHighestEligiblePermission(
    userAuth?.eligible_report_permissions as ReportAccessLevel[] | undefined,
  );
  pendingShareUsers.value = [
    ...pendingShareUsers.value,
    { userOption, accessLevel },
  ];
  selectedUser.value = null;
});

watch(selectedOrganization, async (newOrg) => {
  selectedUser.value = null;
  partnerTeamMembers.value = [];

  if (newOrg && newOrg !== 'own') {
    await getPartnerTeamMembers(newOrg);
  }
});

function isCreator(user: UserWithReportAccess) {
  if (user.user_id !== report.value.created_by_user_id) return false;
  const userOrgId = user.organization_id ?? currentOrg.value.id;
  const reportOrgId = report.value.organization_id ?? currentOrg.value.id;
  return userOrgId === reportOrgId;
}

function canAccessBeEdited(user: UserWithReportAccess) {
  return hasScope('write:reports') && !isCreator(user);
}

function onClearSelected() {
  selectedUser.value = null;
  pendingShareUsers.value = [];
  showShareErrorMessage.value = false;
  loadingState.value = {};
  generalAccessLevel.value = savedGeneralAccessLevel.value;
}

function onPendingUserRoleChange(index: number, value: ReportAccessLevel) {
  const list = [...pendingShareUsers.value];
  if (list[index]) {
    list[index] = { ...list[index], accessLevel: value };
    pendingShareUsers.value = list;
  }
}

function onPendingRowUserChange(rowIndex: number, userId: number) {
  const options = getAvailableUserOptionsForRow(rowIndex);
  const userOption = options.find((o) => o.value === userId);
  if (!userOption) return;
  const authorizations = isOwnOrgSelected.value
    ? teamMembers.value
    : partnerTeamMembers.value;
  const userAuth = authorizations.find((auth) => auth.user?.id === userId);
  const accessLevel = getHighestEligiblePermission(
    userAuth?.eligible_report_permissions as ReportAccessLevel[] | undefined,
  );
  const list = [...pendingShareUsers.value];
  if (list[rowIndex]) {
    list[rowIndex] = { userOption, accessLevel };
    pendingShareUsers.value = list;
  }
}

const canAddMorePeople = computed(() => availableUserOptions.value.length > 0);

const addMoreWrapperComponent = computed(() =>
  canAddMorePeople.value ? 'div' : BittsTooltip,
);

const addMoreWrapperProps = computed(() =>
  canAddMorePeople.value
    ? {}
    : { trigger: 'hover', placement: 'top', overlayClass: 'w-max' },
);

function onAddMorePeopleClick() {
  const first = availableUserOptions.value[0];
  if (first) {
    selectedUser.value = first.value;
  }
}

const eventSite = computed(() => {
  return route.name === 'edit_report'
    ? EVENT_SITES.REPORT_DETAIL_VIEW
    : EVENT_SITES.REPORTS_LIST_VIEW;
});

function onCopyReportLink() {
  iteratively.userSharesListAction({
    action: 'copied_link',
    report_id: report.value.id,
    event_site: eventSite.value,
    shared_with_user_id: '',
  } as never);
  copyToClipBoard(`${window.location.origin}/reports/${report.value.id}`);
  flashesStore.addSuccessFlash({
    message: 'List link copied to clipboard',
  });
}

function getEligiblePermissionsFromAuthorizations(
  userId: number,
  organizationId: number | null | undefined,
): ReportAccessLevel[] | undefined {
  const orgId = organizationId ?? currentOrg.value.id;
  const isOwnOrganization = !orgId || orgId === currentOrg.value.id;
  const list = isOwnOrganization ? teamMembers.value : partnerTeamMembers.value;
  return list.find((a) => a.user?.id === userId)
    ?.eligible_report_permissions as ReportAccessLevel[] | undefined;
}

function resolveEligibleReportPermissionsForMergedUserAccess(options: {
  apiUserAccess: ReportUserAccessWithPartner;
  mergedOrganizationId: number | null | undefined;
  existingEntry?: ReportUserAccessWithPartner;
  authorizationEligible?: ReportAccessLevel[] | undefined;
}): ReportAccessLevel[] | undefined {
  const {
    apiUserAccess,
    mergedOrganizationId,
    existingEntry,
    authorizationEligible,
  } = options;

  return (
    apiUserAccess.eligible_report_permissions ??
    (existingEntry
      ? existingEntry.eligible_report_permissions
      : (authorizationEligible ??
        getEligiblePermissionsFromAuthorizations(
          apiUserAccess.user_id,
          mergedOrganizationId,
        )))
  );
}

function getEligiblePermissionsForPendingUser(
  pendingUser: PendingShareUser,
): ReportAccessLevel[] {
  const eligible = getEligiblePermissionsFromAuthorizations(
    pendingUser.userOption.user_id,
    pendingUser.userOption.organization_id,
  );
  return eligible?.length ? eligible : ['viewer'];
}

async function onSaveGeneralAccess() {
  if (!report.value.id || !generalAccessLevel.value) return;
  isSavingGeneralAccess.value = true;
  try {
    const { error } = await crossbeamApi.PUT('/v0.1/reports/general-access', {
      body: {
        report_ids: [report.value.id],
        access_level: generalAccessLevel.value,
      },
    });
    if (error) {
      flashesStore.addErrorFlash({
        message: 'Failed to update general access. Please try again.',
      });
      return;
    }
    savedGeneralAccessLevel.value = generalAccessLevel.value;
    report.value.general_access = generalAccessLevel.value;
    flashesStore.addSuccessFlash({ message: 'General access updated.' });
    iteratively.userSharesListAction({
      action: 'changed_general_access',
      report_id: report.value.id,
      event_site: eventSite.value,
      general_access: generalAccessLevel.value,
    } as never);
    emit('close');
  } finally {
    isSavingGeneralAccess.value = false;
  }
}

function onCancelGeneralAccess() {
  generalAccessLevel.value = savedGeneralAccessLevel.value;
  emit('close');
}

async function onClickedSendInvite() {
  if (pendingShareUsers.value.length === 0) return;

  showShareErrorMessage.value = false;
  const pendingSnapshot = pendingShareUsers.value;
  isLoading.value = true;

  const userAccessEntries = pendingSnapshot.map((p) => ({
    user_id: p.userOption.user_id,
    access_level: p.accessLevel,
    organization_id: p.userOption.organization_id,
  }));

  let shareData: { user_access?: typeof report.value.user_access };
  if (isOwnOrgSelected.value) {
    const { data, error } = await crossbeamApi.PUT(
      '/v0.1/reports/bulk-user-access',
      {
        body: {
          report_ids: [report.value.id],
          user_access: userAccessEntries.map(({ user_id, access_level }) => ({
            user_id,
            access_level,
          })),
        },
      },
    );
    if (error) {
      isLoading.value = false;
      showShareError(error);
      throw error;
    }
    const reportEntry = data.reports.find(
      (r) => r.report_id === report.value.id,
    );
    shareData = { user_access: reportEntry?.user_access };
  } else {
    const { data, error } = await crossbeamApi.POST(
      '/v0.2/reports/{uuid}/user-access',
      {
        body: {
          user_access: userAccessEntries,
        },
        params: {
          path: { uuid: report.value.id },
        },
      },
    );
    if (error) {
      isLoading.value = false;
      showShareError(error);
      throw error;
    }
    shareData = data;
  }
  await handleShareSuccess(pendingSnapshot, shareData);
}

function showShareError(error: unknown) {
  if (isReportUserAccessUnsharedColumnsError(error)) {
    showShareErrorMessage.value = true;
  } else {
    showShareErrorMessage.value = false;
    flashesStore.addErrorFlash({
      message: 'Failed to share list. Please try again.',
    });
  }
}

async function handleShareSuccess(
  pendingSnapshot: PendingShareUser[],
  data: { user_access?: typeof report.value.user_access },
) {
  showShareErrorMessage.value = false;

  pendingSnapshot.forEach((p) => {
    iteratively.userSharesListAction({
      action: 'shared_with_user',
      report_id: report.value.id,
      event_site: eventSite.value,
      shared_with_user_id: String(p.userOption.user_id),
    } as never);
  });

  const existingUserAccess = (report.value.user_access ||
    []) as ReportUserAccessWithPartner[];
  const authorizations = isOwnOrgSelected.value
    ? teamMembers.value
    : partnerTeamMembers.value;

  if (data.user_access) {
    const newUserAccess = data.user_access.map((ua) => {
      const userAccess = ua as ReportUserAccessWithPartner;
      const existingEntry = existingUserAccess.find((eua) => {
        if (eua.user_id !== userAccess.user_id) return false;
        if (userAccess.organization_id == null) return true;
        return eua.organization_id === userAccess.organization_id;
      });
      const pending = pendingSnapshot.find(
        (p) =>
          p.userOption.user_id === userAccess.user_id &&
          (userAccess.organization_id ?? existingEntry?.organization_id) ===
            p.userOption.organization_id,
      );
      const userAuth = authorizations.find(
        (auth) => auth.user?.id === pending?.userOption.user_id,
      );
      let fullUserObject = userAuth?.user ?? existingEntry?.user ?? null;
      if (!fullUserObject && pending) {
        fullUserObject = {
          id: pending.userOption.user_id,
          first_name: pending.userOption.first_name,
          last_name: pending.userOption.last_name,
          email: pending.userOption.email,
          picture_url: pending.userOption.picture_url ?? null,
          department: pending.userOption.role ?? null,
          company_position: null,
          job_title: null,
          pronouns: null,
        };
      }
      const mergedOrganizationId =
        userAccess.organization_id ?? existingEntry?.organization_id;
      return {
        ...userAccess,
        organization_id: mergedOrganizationId,
        user: userAccess.user ?? fullUserObject ?? existingEntry?.user,
        eligible_report_permissions:
          resolveEligibleReportPermissionsForMergedUserAccess({
            apiUserAccess: userAccess,
            mergedOrganizationId,
            existingEntry,
            authorizationEligible: existingEntry
              ? undefined
              : (userAuth?.eligible_report_permissions as
                  | ReportAccessLevel[]
                  | undefined),
          }),
      } as ReportUserAccessWithPartner;
    });

    const responseUserKeys = new Set(newUserAccess.map(getUserAccessKey));
    const hasAllUsers =
      existingUserAccess.length === 0 ||
      existingUserAccess.every((ua) =>
        responseUserKeys.has(getUserAccessKey(ua)),
      );

    if (hasAllUsers) {
      report.value.user_access =
        newUserAccess as typeof report.value.user_access;
    } else {
      const mergedUserAccess = [
        ...existingUserAccess.filter(
          (ua) => !responseUserKeys.has(getUserAccessKey(ua)),
        ),
        ...newUserAccess,
      ];
      report.value.user_access =
        mergedUserAccess as typeof report.value.user_access;
    }
  }

  await getTeamMembers();
  if (!isOwnOrgSelected.value) {
    await getPartnerOrgsWithAccess();
  }

  const count = pendingSnapshot.length;
  const names = pendingSnapshot.map((p) =>
    getUserLabel(
      p.userOption.first_name,
      p.userOption.last_name,
      p.userOption.email,
    ),
  );
  onClearSelected();
  isLoading.value = false;
  flashesStore.addSuccessFlash({
    message:
      count === 1
        ? `${names[0]} added to List`
        : `${count} people added to List`,
  });
}

async function onClickedRemoveUser(option: Option) {
  const userId = Number(option.user.user_id);
  const userOrgId = option.user.organization_id;
  const loadingKey = getUserAccessKey(userId, userOrgId);
  loadingState.value[loadingKey] = true;

  if (option.value === 'remove_user') {
    const { error } = await crossbeamApi.DELETE(
      '/v0.2/reports/{uuid}/user-access',
      {
        body: {
          user_access: [
            {
              user_id: userId,
              organization_id: userOrgId ?? currentOrg.value.id,
            },
          ],
        } as ReportUserAccessDeletePayload,
        params: {
          path: { uuid: report.value.id },
        },
      },
    );
    loadingState.value[loadingKey] = false;

    if (error) {
      flashesStore.addErrorFlash({
        message: `${getUserLabel(option.user.first_name, option.user.last_name, option.user.email)} could not be removed. Contact Crossbeam for help.`,
      });
      throw error;
    }

    const existingUserAccess = report.value.user_access || [];
    report.value.user_access = existingUserAccess.filter(
      (ua) =>
        !(
          ua.user_id === userId &&
          (ua.organization_id ?? currentOrg.value.id) ===
            (userOrgId ?? currentOrg.value.id)
        ),
    );
    flashesStore.addSuccessFlash({
      message: `${getUserLabel(option.user.first_name, option.user.last_name, option.user.email)} removed from List`,
    });
  } else {
    const newAccessLevel = option.value as ReportAccessLevel;
    const { data, error } = await crossbeamApi.PATCH(
      '/v0.2/reports/{uuid}/user-access',
      {
        body: {
          user_access: [
            {
              user_id: userId,
              organization_id: userOrgId ?? currentOrg.value.id,
              access_level: newAccessLevel,
            },
          ],
        },
        params: {
          path: { uuid: report.value.id },
        },
      },
    );
    loadingState.value[loadingKey] = false;

    if (error) {
      const isUnsharedColumnsError =
        isReportUserAccessUnsharedColumnsError(error);
      flashesStore.addErrorFlash(
        isUnsharedColumnsError
          ? {
              message: UNSHARED_COLUMNS_ERROR_HEADER,
              description: UNSHARED_COLUMNS_ERROR_DESCRIPTION,
            }
          : {
              message: `${getUserLabel(option.user.first_name, option.user.last_name, option.user.email)} access could not be updated. Contact Crossbeam for help.`,
            },
      );
      throw error;
    }

    if (data?.user_access) {
      const existingUserAccess = (report.value.user_access ||
        []) as ReportUserAccessWithPartner[];

      const newUserAccess = data.user_access.map((ua) => {
        const userAccess = ua as ReportUserAccessWithPartner;
        const existingEntry = existingUserAccess.find((eua) => {
          if (eua.user_id !== userAccess.user_id) return false;
          if (userAccess.organization_id == null) return true;
          return eua.organization_id === userAccess.organization_id;
        });
        const mergedOrganizationId =
          userAccess.organization_id ?? existingEntry?.organization_id;

        return {
          ...userAccess,
          organization_id: mergedOrganizationId,
          user: userAccess.user ?? existingEntry?.user ?? undefined,
          eligible_report_permissions:
            resolveEligibleReportPermissionsForMergedUserAccess({
              apiUserAccess: userAccess,
              mergedOrganizationId,
              existingEntry,
            }),
        } as ReportUserAccessWithPartner;
      });

      const responseUserKeys = new Set(newUserAccess.map(getUserAccessKey));
      const hasAllUsers =
        existingUserAccess.length === 0 ||
        existingUserAccess.every((ua) =>
          responseUserKeys.has(getUserAccessKey(ua)),
        );

      if (hasAllUsers) {
        report.value.user_access =
          newUserAccess as typeof report.value.user_access;
      } else {
        const mergedUserAccess = [
          ...existingUserAccess.filter(
            (ua) => !responseUserKeys.has(getUserAccessKey(ua)),
          ),
          ...newUserAccess,
        ];
        report.value.user_access =
          mergedUserAccess as typeof report.value.user_access;
      }
    }
    flashesStore.addSuccessFlash({
      message: `${getUserLabel(option.user.first_name, option.user.last_name, option.user.email)}'s access updated to ${option.display.toLowerCase()}`,
    });
  }
}

const isReportFromExternalOrg = computed(() => {
  return (
    report.value?.organization_id &&
    report.value.organization_id !== currentOrg.value.id
  );
});

onMounted(async () => {
  try {
    const promises = [getTeamMembers()];
    if (!isReportFromExternalOrg.value) {
      promises.push(getPartnerOrgsWithAccess());
    }
    await Promise.all(promises);
  } finally {
    isLoadingAccess.value = false;
  }
});
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.report-access-selection-controls {
  @apply p-16 w-[480px] max-h-[750px] flex flex-col;
}

.report-access-selection-controls__header {
  @apply flex-shrink-0;
}

.report-access-selection-controls__scrollable {
  @apply overflow-auto flex-1 min-h-[100px];
}

.report-access-selection-controls__footer {
  @apply flex-shrink-0;
}

.report-access-selection-controls__pending-row {
  @apply flex items-center gap-8 mb-12;
}

.report-access-selection-controls__pending-user-picker {
  @apply flex-1 min-w-0;
}

.report-access-selection-controls__pending-row
  .report-access-selection-controls__role-picker
  :deep(.ant-select-selector) {
  height: 40px;
  min-height: 40px;
}

.report-access-selection-controls__role-picker {
  @apply w-[115px] shrink-0;
}

.report-access-selection-controls__add-more-wrap {
  @apply mt-2 mb-4;
}

.report-access-selection-controls__add-more-button {
  @apply text-neutral-accent;
}

.report-access-user-option__name {
  @apply font-medium text-neutral-text-strong truncate;
}

.report-access-selection-controls
  :deep(.ant-select-selection-item .report-access-user-option) {
  .bitts-avatar.mr-10 {
    margin-right: 8px !important;
  }

  .bitts-avatar--icon.bitts-avatar__small {
    width: 16px !important;
    height: 16px !important;
    line-height: 16px !important;
    font-size: 12px !important;
    min-width: 16px !important;
  }

  .report-access-user-option__email {
    display: none !important;
  }

  .report-access-user-option__role {
    display: none !important;
  }

  .report-access-user-option__name {
    font-weight: unset !important;
    font-size: unset !important;
    line-height: unset !important;
  }
}
</style>

<style lang="pcss">
@reference "../../css/theme.css";
.report-access-selection-controls__dropdown.ant-select-dropdown
  .ant-select-item-option-active:not(.ant-select-item-option-disabled),
.report-access-selection-controls__dropdown.ant-select-dropdown
  .ant-select-item-option-selected:not(.ant-select-item-option-disabled),
.report-access-selection-controls__dropdown.ant-select-dropdown
  .ant-select-item-option:hover:not(.ant-select-item-option-disabled) {
  @apply !bg-neutral-background-weak;
}

.c-report-access-selection-controls__tooltip {
  .ant-tooltip-inner {
    @apply w-[240px];
  }
}
</style>
